import { zodResolver } from '@hookform/resolvers/zod'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat'
import { z } from 'zod'

import { FormField } from '@/app/component/form/field'
import SubmitButton from '@/app/component/form/submit-button'
import { useResetPassword } from '@/app/module/auth/hooks/use-reset-password'

type ResetPasswordFormValues = {
  password1: string
  password2: string
}

const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [resetPassword, { isLoading }] = useResetPassword()

  const key = searchParams.get('key')

  const methods = useForm<ResetPasswordFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  })

  const handleSubmit = async (data: ResetPasswordFormValues) => {
    const success = await resetPassword({
      ...data,
      key,
    })
    if (success) {
      navigate('/login')
    }
  }

  return (
    <StyledCard>
      <StyledCardContent>
        <StyledIcon>account_circle</StyledIcon>
        <Typography color="textSecondary" variant="caption" mb={1}>
          Please enter your new password.
        </Typography>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)} noValidate>
            {Object.values(fields).map((field) => (
              <FormField key={field.fieldProps.name} {...field} />
            ))}
            <StyledActions>
              <SubmitButton isDirty={methods.formState.isDirty} isSubmitting={isLoading}>
                Reset Password
              </SubmitButton>
            </StyledActions>
          </form>
        </FormProvider>
      </StyledCardContent>
    </StyledCard>
  )
}

const defaultValues = {
  password1: '',
  password2: '',
}

const fields = {
  password1: {
    fieldProps: { label: 'New Password', name: 'password1', type: 'password', required: true },
    type: 'password',
  },
  password2: {
    fieldProps: {
      label: 'Confirm New Password',
      name: 'password2',
      type: 'password',
      required: true,
      info: 'Use at least 15 characters.',
    },
    type: 'password',
  },
} as const

const schema = z
  .object({
    password1: z.string().min(15, { message: 'Password must be at least 15 characters long' }),
    password2: z.string().min(1, { message: 'Required' }),
  })
  .refine((data) => data.password2.length >= 15, {
    message: 'Password must be at least 15 characters long',
    path: ['password2'],
  })
  .refine((data) => data.password1 === data.password2, {
    message: 'Passwords have to match',
    path: ['password2'],
  })

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
})

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '2rem',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  margin: 'auto',
  marginBottom: '15px',
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

const StyledActions = styled(CardActions)({
  justifyContent: 'flex-end',
  paddingBottom: '1rem',
  paddingLeft: 0,
  paddingRight: 0,
})

export default ResetPasswordForm
